import range from 'lodash/range';
import sortBy from "lodash/sortBy";
import orderBy from "lodash/orderBy";
import moment from 'moment';

export const getHydrationStatus = (state) => {
    return state.hasHydration;
};

export const needs = (state, getters) => {
    let needs = {};
    
    state.program?.needs?.forEach(need => {
        needs[need.id] = {
            id: need.id,
            isFavorited: need.isFavorited,
            program_id: state.program.id,
            grad_year: need.grad_year,
            match_status: need.match_status,
            position: need.position,
            published_at: need.published_at,
            notes: need.notes,
            program_need_criteria_fields: [],
            sport_fields: [],
        };
        
        need.criteria.forEach(criteria => {
            criteria.field_label = getters.getFormattedFieldLabel(criteria);
            criteria.formatted_criteria = getters.getFormattedCriteriaLabel(criteria);
            needs[need.id][`${criteria.field_type}s`].push(criteria);
        });
        
        needs[need.id] = getters.sortNeedCriteriaFields(needs[need.id]);
    });
    
    return needs;
};

export const publishedNeeds = (state, getters) => {
    return Object.values(getters.sortedNeeds).filter(({published_at}) => !!published_at);
};

export const sortedNeeds = (state, getters) => {
    const sortedNeeds = orderBy(getters.needs, ['grad_year', 'position']);
    const fullMatch = sortedNeeds.filter(need => need.match_status == 'full');
    const potentialMatch = sortedNeeds.filter(need => need.match_status == 'potential');
    const nonMatch = sortedNeeds.filter(need => need.match_status !== 'full' && need.match_status !== 'potential');

    return [...fullMatch, ...potentialMatch, ...nonMatch];
};

/**
 * Reduce the program state into an array of grad years and subsequent needs
 * @param state
 * @returns {T}
 */
export const getGradYears = (state) => {
    if (state.isFetchingProgram) {
        return [];
    }
    // 1/1-8/31; available years = Current Year through Current Year + 3
    // 9/1-12/31; available years = Next Year through Current Year + 4
    // Adjust these parameters if biz requirements change
    const today = moment();
    const earlyYearStart = moment('01-01', 'MM-DD');
    const earlyYearEnd   = moment('08-31', 'MM-DD');
    const lateYearStart  = moment('09-1', 'MM-DD');
    const lateYearEnd  = moment('12-31', 'MM-DD');

    let years = [];
    const currentYear = moment().year();

    if (today.isBetween(earlyYearStart, earlyYearEnd, 'day', '[]')) {
        years = range(currentYear, currentYear + 4); // _.range is an exclusive operation so inc end by 1
    } else if (today.isBetween(lateYearStart, lateYearEnd, 'day', '[]')) {
        years = range(currentYear + 1, currentYear + 5);
    }


    if (years.length === 0) {
        throw new Error('Invalid date range');
    }


    let gradYears = [];
    years.forEach(year => {
        const data = {
            year,
            positions: state.program.positions,
        };
        gradYears.push(data);
    });
    return gradYears;
};

/**
 * Check if a need already exists in the state.
 * @param state
 * @param needSlug
 */
export const checkNeedExists = (state) => (needSlug) => {
    // Build an array of all our needs with the id.year notation
    const formattedNeeds = state.program.needs.map((need) => {
        return `${need.sport_option_id}.${need.grad_year}`;
    });
    return formattedNeeds.includes(needSlug);
};

export const getFormattedFieldLabel = (state, getters) => (criteria) => {
    if (criteria.field_name === 'vpi_pct') {
        return 'VPI';    
    }
    
    return criteria.field_label;
}; 

export const getFormattedCriteriaLabel = (state, getters) => (criteria) => {
    return criteria.field_type === 'program_need_criteria_field' ? 
        getters.getFormattedProgramNeedFieldCriteriaLabel(criteria) :
        getters.getFormattedSportFieldCriteriaLabel(criteria);
}; 

export const getFormattedSportFieldCriteriaLabel = (state, getters) => (criteria) => {
    let unit;
    let value = criteria.value;
    
    if (!criteria.field_unit) {
        criteria.field_unit = state.program.sport.sportFields.find( ({field_name}) => field_name === criteria.field_name).unit;
    }
    
    switch (criteria.field_unit) {
        case 'inches':
            unit = '';
            break;
        case 'pounds':
            unit = 'lbs';
            break;
        case 'meters':
            unit = 'm';
            break;
        case 'seconds':
            unit = 'sec';
            break;
        default:
            unit = criteria.field_unit ? criteria.field_unit : '';
    }
    
    if (criteria.min && !criteria.max) {
        value = `${getters.isFeetAndInchesField(criteria.field_name) ? `${getters.getFormattedFeetAndInches(criteria.min, criteria.field_name)}+` : `${parseFloat(criteria.min)}+`}`;
    }
    
    if (criteria.max && !criteria.min) {
        value = `\u{02264}${getters.isFeetAndInchesField(criteria.field_name) ? getters.getFormattedFeetAndInches(criteria.max, criteria.field_name) : parseFloat(criteria.max)}`;
    }
    
    if (criteria.min && criteria.max) {
        let min = parseFloat(criteria.min);
        let max = parseFloat(criteria.max);
        
        if (getters.isFeetAndInchesField(criteria.field_name)) {
            min = getters.getFormattedFeetAndInches(criteria.min, criteria.field_name);
            max = getters.getFormattedFeetAndInches(criteria.max, criteria.field_name);
        }
        
        value = `${min} - ${max}`;
    }
    
    return `${value}${unit}`;
};

export const getFormattedProgramNeedFieldCriteriaLabel = (state) => (criteria) => {
    if (criteria.field_name === 'video_required') {
        return 'Required';    
    }
    
    return `${Number(criteria.min).toFixed(criteria.field_name === 'gpa' ? 2 : 0)}+`;
};

export const isFeetAndInchesField = (state) => (fieldName) => {
  return state.feetAndInchesFields.includes(fieldName);
};

export const convertValuesToInches = (state, getters) => (values) => {
  Object.keys(values).map(function (key, index) {
    values[key] = getters.convertValueToInches(values[key]);
  });

  return values;
};

export const convertValuesToFeet = (state, getters) => (values) => {
  Object.keys(values).map(function (key, index) {
    values[key] = getters.convertValueToFeet(values[key]);
  });

  return values;
};

export const convertValueToFeet = (state) => (value) => {
  
  return value !== null ? value : null;
};

export const convertValueToInches = (state) => (value) => {
  return value !== null ? value : null;
};

export const getFormattedFeetAndInches = (state, getters) => (totalInches, fieldName) => {
    if (fieldName == 'vertical_inches') return `${Math.trunc(totalInches)}\u{0201D}`;

    let feet = Math.trunc(totalInches / 12);
    let inches = totalInches % 12;

    return `${feet}\u{02019}${inches}\u{0201D}`;
};

export const sortNeedCriteriaFields = (state) => (data) => {
    Object.keys(data).map(i => {
        if (i === 'sport_fields') {
            data[i] = sortBy(data[i], field => {
                return state.fieldOrder.indexOf(field.field_name);
            });
        }
        
        if (i === 'program_need_criteria_fields') {
            data[i] = sortBy(data[i], field => {
                return ['gpa', 'sat', 'act'].indexOf(field.field_name);
            });
        }
    });
    
    return data;
};

export const programHasPositions = (state) => {
    if (!state.program.positions) {
        return false;
    }
    
    return Object.keys(state.program.positions).length > 0;
};

export const getProgramImage = (state) => {
    if (!state.program.id) {
        return false;
    }
    
    let image = state.program.college.images.filter(image => image.isProfile)[0];
    
    if (!image) {
        image = state.program.college.images[0];
    }
    
    return image?.url;
};

